import { WebSocketManager } from '../websocket/WebSocketManager';
import { Message, MessageType } from '../websocket/types';
import { TemplateApi } from '../../features/template/api/templateApi';

export interface TaskMonitorConfig {
  taskId: string;
  pollInterval?: number;
  maxRetries?: number;
  onMessage?: (message: Message) => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
  onConnectionStateChange?: (state: MonitoringState) => void;
  onStageComplete?: (stageName: string, result: Record<string, unknown>) => void;
}

export type MonitoringState = 
  | { type: 'initializing' }
  | { type: 'connecting_websocket' }
  | { type: 'dual_monitoring', lastWsMessage?: Message, lastRestStatus?: TaskStatus }
  | { type: 'websocket_only' }
  | { type: 'rest_only' }
  | { type: 'completed', result: TaskResult }
  | { type: 'failed', error: Error };

interface TaskStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  result?: TaskResult;
  error?: string;
}

interface TaskResult {
  [key: string]: unknown;
}

const EMPTY_RESULT: TaskResult = {};

export class TaskMonitor {
  private wsManager: WebSocketManager | null = null;
  private pollTimer: NodeJS.Timeout | null = null;
  private messageQueue: Message[] = [];
  private hasReceivedWsMessage = false;
  private isCompleted = false;
  private currentState: MonitoringState = { type: 'initializing' };
  private retryCount = 0;

  constructor(private config: TaskMonitorConfig, wsManager: WebSocketManager) {
    if (!wsManager) {
      throw new Error('WebSocketManager is required for TaskMonitor');
    }
    this.config.pollInterval = config.pollInterval || 5000;
    this.config.maxRetries = config.maxRetries || 3;
    this.wsManager = wsManager;
  }

  private setState(newState: MonitoringState) {
    this.currentState = newState;
    this.config.onConnectionStateChange?.(newState);
  }

  private debugLog(message: string, extra: Record<string, unknown> = {}) {
    console.log(`[TaskMonitor] ${message}`, {
      taskId: this.config.taskId,
      state: this.currentState,
      hasReceivedWsMessage: this.hasReceivedWsMessage,
      isCompleted: this.isCompleted,
      ...extra,
      timestamp: new Date().toISOString(),
    });
  }

  public async start(): Promise<void> {
    this.debugLog('Starting task monitor');
    
    try {
      // Start both monitoring methods in parallel
      await Promise.all([
        this.startWebSocket(),
        this.startPolling()
      ]);
      this.debugLog('Task monitor start: All monitoring methods initiated successfully');
    } catch (error) {
      this.handleConnectionError(error instanceof Error ? error : new Error('Failed to start monitoring'));
    }
  }

  private async startWebSocket(): Promise<void> {
    this.debugLog('Starting WebSocket connection');
    this.setState({ type: 'connecting_websocket' });

    try {
      if (!this.wsManager) {
        throw new Error('Centralized WebSocketManager instance is required but not provided');
      }
      
      // Handle WebSocket protocol events
      this.wsManager.onError = this.handleConnectionError.bind(this);
      this.wsManager.onMessage = this.handleWebSocketMessage.bind(this);
      this.wsManager.onMaxRetriesExceeded = () => {
        this.handleConnectionError(new Error('WebSocket connection retries exceeded'));
      };
      
      await this.wsManager.connect();
      this.debugLog('WebSocket connected');
      
      if (!this.hasReceivedWsMessage) {
        this.setState({ type: 'dual_monitoring' });
      }
    } catch (error) {
      this.debugLog('WebSocket connection failed', { error });
      // Fall back to REST-only mode if WebSocket fails
      this.setState({ type: 'rest_only' });
    }
  }

  private async startPolling(): Promise<void> {
    this.debugLog('Starting REST polling');
    await this.pollTaskStatus();
  }

  private handleWebSocketMessage(message: Message): void {
    this.debugLog('Received WebSocket message', { 
      messageType: message.type,
      isCompleted: this.isCompleted,
      hasReceivedWsMessage: this.hasReceivedWsMessage
    });
    
    // Don't process messages if we're already completed
    if (this.isCompleted) {
      this.debugLog('Ignoring message - already completed');
      return;
    }

    if (!this.hasReceivedWsMessage) {
      this.hasReceivedWsMessage = true;
      this.setState({ type: 'websocket_only' });
    }

    // Add message to queue for processing
    this.messageQueue.push(message);
    this.processMessageQueue();

    // Update state with last message for dual monitoring
    if (this.currentState.type === 'dual_monitoring') {
      this.setState({ 
        type: 'dual_monitoring', 
        lastWsMessage: message,
        lastRestStatus: this.currentState.lastRestStatus 
      });
    }

    // Handle completion message
    if (message.type === MessageType.COMPLETION) {
      this.handleCompletion(message);
    }
  }

  private async pollTaskStatus(): Promise<void> {
    if (this.isCompleted || this.pollTimer) {
      return;
    }

    try {
      const status = await TemplateApi.getTaskStatus(this.config.taskId);
      const mappedStatus: TaskStatus = {
        status: status.status.toLowerCase() as TaskStatus['status'],
        result: status.result,
        error: status.error
      };
      
      this.debugLog('Polled task status', { status: mappedStatus });
      // Reset retry count on successful poll
      this.retryCount = 0;

      // Update state with last status for dual monitoring
      if (this.currentState.type === 'dual_monitoring') {
        this.setState({
          type: 'dual_monitoring',
          lastWsMessage: this.currentState.lastWsMessage,
          lastRestStatus: mappedStatus
        });
      }

      // Handle completion states from REST API
      if (mappedStatus.status === 'completed' || mappedStatus.status === 'failed') {
        this.handleCompletion({
          type: MessageType.COMPLETION,
          task_id: this.config.taskId,
          processing_type: 'TASK_MONITORING',
          status: mappedStatus.status === 'completed' ? 'SUCCESSFUL' : 'WITH_ERROR',
          data: mappedStatus.result || {},
          error: mappedStatus.error ? { message: mappedStatus.error } : undefined
        });
      } else if (!this.hasReceivedWsMessage || this.currentState.type === 'rest_only') {
        // Continue polling if we're still waiting for WebSocket or in REST-only mode
        const interval = this.config.pollInterval || 5000;
        this.pollTimer = setTimeout(() => {
          this.pollTimer = null;
          this.pollTaskStatus();
        }, interval);
      }
    } catch (error) {
      this.debugLog('Poll failed', { error, retryCount: this.retryCount });
      
      if (this.retryCount < (this.config.maxRetries || 3)) {
        this.retryCount++;
        // Retry with exponential backoff
        const retryInterval = this.config.pollInterval || 5000;
        this.pollTimer = setTimeout(() => {
          this.pollTimer = null;
          this.pollTaskStatus();
        }, retryInterval * Math.pow(2, this.retryCount));
      } else if (this.currentState.type === 'rest_only') {
        // Only show error if we're in REST-only mode and have no other way to get results
        this.setState({ 
          type: 'failed', 
          error: new Error('Unable to retrieve analysis results. Please try again.')
        });
        this.config.onError?.(new Error('Unable to retrieve analysis results. Please try again.'));
        this.stop();
      } else {
        // If we have WebSocket connection, just log the error and continue
        this.debugLog('Max polling retries reached, but WebSocket is available', { 
          currentState: this.currentState 
        });
      }
    }
  }

  private processMessageQueue(): void {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      if (message) {
        // Forward all messages to the client, including ERROR messages
        this.config.onMessage?.(message);
      }
    }
  }

  private handleCompletion(message: Message): void {
    // Don't process if already completed
    if (this.isCompleted) {
      this.debugLog('Ignoring completion message - already completed');
      return;
    }

    this.debugLog('Handling completion', { message });

    if (message.type === MessageType.COMPLETION) {
      const result: TaskResult = 'data' in message && message.data ? message.data : EMPTY_RESULT;
      
      // Set state and notify before cleanup
      this.setState({ type: 'completed', result });
      this.config.onComplete?.();
      
      // Mark as completed and cleanup
      this.isCompleted = true;
      this.stop();
    }
  }

  private handleConnectionError(error: Error): void {
    this.debugLog('Handling connection error', { error });
    
    if (!this.isCompleted) {
      this.setState({ type: 'failed', error });
      this.config.onError?.(error);
    }
    
    // Always ensure cleanup on error
    this.stop();
  }

  public stop(): void {
    this.debugLog('Stopping task monitor - stop() invoked');

    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
      this.debugLog('Polling timer cleared');
    }

    if (this.wsManager) {
      this.debugLog('wsManager exists in stop(), about to call disconnect()', { wsManagerState: this.wsManager });
      console.log('[TaskMonitor] Calling disconnect() on WebSocketManager from stop()...');
      this.wsManager.disconnect();
      console.log('[TaskMonitor] disconnect() has been called on WebSocketManager from stop().');
      this.wsManager = null;
      this.debugLog('wsManager set to null after disconnect()');
    } else {
      this.debugLog('No wsManager instance present in stop()');
    }
  }
} 